import {
  notificationActionResponseSchema,
  notificationMatchesVisitedPath,
  type NotificationListResponse,
} from '@gb-ticket/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { apiFetch } from '@/lib/api';
import {
  notificationsQueryKey,
  useNotifications,
} from '@/features/notifications/use-notifications';

function looksLikeNotifiableContentPath(path: string): boolean {
  return (
    /\/tickets\/\d+/.test(path) ||
    path.startsWith('/intranet/nachrichten/') ||
    path.startsWith('/intranet/wissensdatenbank/') ||
    path.startsWith('/intranet/bibliothek/') ||
    path.startsWith('/intranet/chat/')
  );
}

/**
 * Clears bell entries when the user opens the linked ticket, post or chat
 * (without requiring a click inside the notification dropdown).
 */
export function useAutoMarkNotificationsForPath(enabled: boolean) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const list = useNotifications(enabled);
  /** Paths we already asked the API to clear for (avoid refetch loops). */
  const attemptedPath = useRef<string | null>(null);
  const attemptedMatchKey = useRef<string>('');
  const unreadCount = list.data?.unreadCount ?? 0;
  const listData = list.data;

  useEffect(() => {
    attemptedPath.current = null;
    attemptedMatchKey.current = '';
  }, [location.pathname]);

  useEffect(() => {
    if (!enabled || unreadCount === 0) {
      return;
    }

    const path = location.pathname;
    const items = listData?.items ?? [];
    const matching = items.filter(
      (item) => !item.readAt && notificationMatchesVisitedPath(item, path),
    );
    const matchKey = matching
      .map((m) => m.id)
      .sort()
      .join(',');

    if (matching.length === 0) {
      if (!looksLikeNotifiableContentPath(path)) {
        return;
      }
      if (attemptedPath.current === path) {
        return;
      }
    } else if (attemptedPath.current === path && attemptedMatchKey.current === matchKey) {
      return;
    }

    attemptedPath.current = path;
    attemptedMatchKey.current = matchKey;

    if (matching.length > 0) {
      const now = new Date().toISOString();
      const matched = new Set(matching.map((m) => m.id));
      const patchList = (current: NotificationListResponse | undefined) => {
        if (!current?.items) {
          return current;
        }
        let cleared = 0;
        const nextItems = current.items.map((item) => {
          if (matched.has(item.id) && !item.readAt) {
            cleared += 1;
            return { ...item, readAt: now };
          }
          return item;
        });
        return {
          ...current,
          items: nextItems,
          unreadCount: Math.max(0, current.unreadCount - cleared),
        };
      };
      queryClient.setQueryData(notificationsQueryKey, patchList);
      queryClient.setQueriesData<NotificationListResponse>(
        {
          queryKey: notificationsQueryKey,
          predicate: (query) => query.queryKey[1] === 'inbox',
        },
        patchList,
      );
    }

    let cancelled = false;
    void apiFetch('/notifications/read-for-path', notificationActionResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ path }),
    })
      .then(() => {
        if (!cancelled) {
          void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
        }
      })
      .catch(() => {
        /* keep optimistic state; next refetch corrects */
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, listData, location.pathname, queryClient, unreadCount]);
}
